import { FAMILY } from './families.js';

export const DISASTER_TIER = {
  OFFLINE: 'offline',
  PARTIAL: 'partial',
  SOFT: 'soft'
};

export const DISASTER_CATEGORY = {
  GEOPOLITICAL: 'geopolitical',
  POLICY: 'policy',
  ECONOMIC: 'economic',
  SAFETY: 'safety',
  TECHNICAL: 'technical',
  CLIMATE: 'climate',
  SAFETY_POLITICAL: 'safety-political'
};

export const EFFECT_KIND = {
  OUTPUT_MULTIPLIER: 'output-multiplier',
  OPEX_MULTIPLIER: 'opex-multiplier',
  DEMAND_ADD: 'demand-add',
  SUBSIDY_REMOVE: 'subsidy-remove'
};

export const TARGET_KIND = {
  PLANTS: 'plants',
  FAMILY: 'family',
  ALL_VARIABLE: 'all-variable',
  LARGEST_NON_NUCLEAR_FAMILY: 'largest-non-nuclear-family',
  LARGEST_FAMILY: 'largest-family',
  PLAYER: 'player'
};

export const DISASTERS = [
  {
    id: 'fossil-fuel-supply-crisis',
    number: 1,
    name: 'Gas & Oil Supply Crisis',
    description: 'A geopolitical disruption curtails diesel and gas fuel supply.',
    imageSrc: '/disaster-images/gas-supply-crisis.webp',
    tier: DISASTER_TIER.OFFLINE,
    category: DISASTER_CATEGORY.GEOPOLITICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['diesel-generator', 'combined-cycle-gas']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0 },
    twoTierSeverity: true,
    severity: {
      mild: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 },
      severe: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0 }
    }
  },
  {
    id: 'grid-connection-constraint',
    number: 2,
    name: 'Grid Connection Constraint',
    description: 'Connection bottlenecks permanently reduce output from the most concentrated technology family.',
    imageSrc: '/disaster-images/grid connection constraint.webp',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.TECHNICAL,
    target: { kind: TARGET_KIND.LARGEST_FAMILY },
    effect: {
      kind: EFFECT_KIND.OUTPUT_MULTIPLIER,
      factor: 0.9,
      persistent: true
    }
  },
  {
    id: 'coal-import-ban',
    number: 3,
    name: 'Coal Import Ban',
    description: 'New restrictions limit how much coal can be burned.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.POLICY,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['coal-plant', 'carbon-capture-coal']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.75 }
  },
  {
    id: 'supply-chain-bottleneck',
    number: 4,
    name: 'Supply Chain Bottleneck',
    description:
      'A shortage of critical parts and specialist crews hits the most concentrated non-nuclear part of the grid.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.ECONOMIC,
    target: { kind: TARGET_KIND.LARGEST_NON_NUCLEAR_FAMILY },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.6 }
  },
  {
    id: 'fossil-fuel-price-shock',
    number: 5,
    name: 'Fossil Fuel Price Shock',
    description: 'Global fuel markets surge, doubling fossil operating costs.',
    tier: DISASTER_TIER.SOFT,
    category: DISASTER_CATEGORY.ECONOMIC,
    target: { kind: TARGET_KIND.FAMILY, family: FAMILY.FOSSIL_THERMAL },
    effect: { kind: EFFECT_KIND.OPEX_MULTIPLIER, factor: 2 }
  },
  {
    id: 'nuclear-safety-audit',
    number: 6,
    name: 'Nuclear Safety Audit',
    description: 'A precautionary audit reduces nuclear reactor output for the year.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.SAFETY,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['small-modular-nuclear', 'large-nuclear']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.65 }
  },
  {
    id: 'nuclear-public-outcry',
    number: 7,
    name: 'Nuclear Public Outcry',
    description: 'Public protests force the large reactor to operate at half capacity for the year.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.SAFETY_POLITICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['large-nuclear', 'small-modular-nuclear']
    },
    effect: {
      kind: EFFECT_KIND.OUTPUT_MULTIPLIER,
      factor: 0.5,
      factorsByPlantId: {
        'large-nuclear': 0.5,
        'small-modular-nuclear': 0.85
      }
    }
  },
  {
    id: 'solar-panel-recall',
    number: 8,
    name: 'Solar Panel Recall',
    description: 'A manufacturing defect forces a partial recall of solar panels.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.TECHNICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['solar-pv', 'concentrated-solar']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 }
  },
  {
    id: 'wind-turbine-defect',
    number: 9,
    name: 'Wind Turbine Defect',
    description: 'A design flaw across wind turbines requires de-rating.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.TECHNICAL,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['onshore-wind', 'offshore-wind']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 }
  },
  {
    id: 'tidal-equipment-failure',
    number: 10,
    name: 'Tidal Equipment Failure',
    description: 'Submerged tidal generators suffer catastrophic failure.',
    tier: DISASTER_TIER.OFFLINE,
    category: DISASTER_CATEGORY.TECHNICAL,
    target: { kind: TARGET_KIND.PLANTS, ids: ['tidal'] },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0 }
  },
  {
    id: 'severe-drought',
    number: 11,
    name: 'Severe Drought',
    description: 'Reservoirs and rivers run low, reducing hydro output.',
    tier: DISASTER_TIER.PARTIAL,
    category: DISASTER_CATEGORY.CLIMATE,
    target: {
      kind: TARGET_KIND.PLANTS,
      ids: ['large-hydro-dam', 'run-of-river-hydro']
    },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.75 }
  },
  {
    id: 'extreme-weather-event',
    number: 12,
    name: 'Extreme Weather Event',
    description: 'A year of historically poor weather hits every renewable.',
    tier: DISASTER_TIER.SOFT,
    category: DISASTER_CATEGORY.CLIMATE,
    target: { kind: TARGET_KIND.ALL_VARIABLE },
    effect: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.5 },
    twoTierSeverity: true,
    severity: {
      mild: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.6 },
      severe: { kind: EFFECT_KIND.OUTPUT_MULTIPLIER, factor: 0.2 }
    }
  },
  {
    id: 'demand-spike',
    number: 13,
    name: 'Demand Spike',
    description: 'A cold snap or growth surge drives demand higher this year.',
    tier: DISASTER_TIER.SOFT,
    category: DISASTER_CATEGORY.CLIMATE,
    target: { kind: TARGET_KIND.PLAYER },
    effect: {
      kind: EFFECT_KIND.DEMAND_ADD,
      amount: 3,
      amountByYear: { 1: 3, 2: 5, 3: 7 }
    }
  },
  {
    id: 'subsidy-reform',
    number: 14,
    name: 'Subsidy Reform',
    description: 'Fossil fuel subsidies are repealed. Fossil Thermal opex returns to its full rate from this year onward, for the rest of the game.',
    tier: DISASTER_TIER.SOFT,
    category: DISASTER_CATEGORY.POLICY,
    target: { kind: TARGET_KIND.PLAYER },
    effect: { kind: EFFECT_KIND.SUBSIDY_REMOVE }
  }
];

export const DISASTER_BY_ID = Object.fromEntries(
  DISASTERS.map((d) => [d.id, d])
);
